// Ported from exams lib/helpMode.ts — module-level signal + localStorage persistence, storage key renamed "exams:helpMode" → "erp:helpMode"; canonical home for the kit's contextual-help gating (Field.hint et al. import from here) AND the control that flips it (HelpToggle, mounted once in the shell header).
import { createSignal } from "solid-js";
import { CircleHelp } from "lucide-solid";

const KEY = "erp:helpMode";

const read = (): boolean => {
  try {
    return localStorage.getItem(KEY) !== "0";
  } catch {
    return true; // private mode / SSR — default on
  }
};

const [helpMode, setHelpMode] = createSignal(read());

/** Reactive: is contextual help currently shown? Defaults on. */
export { helpMode };

/** Flip help on/off and remember the choice. */
export function toggleHelpMode(): void {
  setHelpMode((v) => {
    const next = !v;
    try {
      localStorage.setItem(KEY, next ? "1" : "0");
    } catch {
      /* private mode / SSR */
    }
    return next;
  });
}

/**
 * The one UI affordance for the help gate — without it `helpMode` is a signal
 * nothing can flip and every `Field hint` is permanently on. Mirrors
 * ThemeToggle: a single icon control in the navbar that owns its own
 * persistence. A real `aria-pressed` toggle button (not a checkbox) because
 * the pressed state IS the message; the accessible name stays constant so
 * assistive tech announces "pressed"/"not pressed" against one label.
 */
export function HelpToggle(props: { id?: string }) {
  return (
    // State ink: the --ui-state-* ramp (tokens.css) — key-focus == hover, down one step darker (Spectrum).
    // Box: size-(--ds-target), the component-height-100 hit floor (32px, 40 at
    // touch); h-7 w-7 froze it at 28px. Fallback = the old box, for a portal
    // without spectrum.css.
    <button
      type="button"
      id={props.id ?? "help-mode-toggle"}
      class="flex size-[var(--ds-target,1.75rem)] items-center justify-center rounded-300 transition-colors hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
      aria-pressed={helpMode() ? "true" : "false"}
      aria-label="Toggle contextual help"
      title={helpMode() ? "Contextual help is on" : "Contextual help is off"}
      onClick={() => toggleHelpMode()}
    >
      {/* Static class per state (no assembled strings) — lit primary while on,
          dimmed while off, so the pressed state reads without colour alone. */}
      <CircleHelp
        size={13}
        class={helpMode() ? "text-primary" : "text-base-subtle hover:text-base-content"}
        aria-hidden="true"
      />
    </button>
  );
}
